import type { ReportsCateringSnapshot } from '@/lib/api/reports';
import { formatDistanceToNow } from 'date-fns';

export interface CateringOverviewContext {
  t: (key: string, options?: { defaultValue?: string; [key: string]: unknown }) => string;
  currencyFormatter: Intl.NumberFormat;
  percentFormatter: Intl.NumberFormat;
}

export interface CateringOverview {
  pipelineValue: string;
  confirmedValue: string;
  outstandingValue: string;
  conversionLabel: string;
  averageGuests: string;
  payments: ReportsCateringSnapshot['payments'];
  totals: ReportsCateringSnapshot['totals'];
  upcoming: Array<{
    id: string;
    name: string;
    status: string;
    budget: string;
    dateLabel: string;
    dueStatus: 'dueSoon' | 'overdue' | null;
  }>;
}

export const buildCateringOverview = (
  snapshot: ReportsCateringSnapshot | null,
  { t, currencyFormatter, percentFormatter }: CateringOverviewContext,
): CateringOverview | null => {
  if (!snapshot) {
    return null;
  }

  const { totals, payments, upcoming } = snapshot;

  const formatAmount = (cents: number) => currencyFormatter.format(cents / 100);
  const pipelineValue = formatAmount(totals.pipelineValueCents);
  const confirmedValue = formatAmount(totals.confirmedValueCents);
  const outstandingValue = formatAmount(payments.outstandingCents);

  const conversionLabel =
    typeof totals.conversionRate === 'number'
      ? percentFormatter.format(Math.max(0, totals.conversionRate))
      : t('dashboard:reports.catering.conversionUnavailable', { defaultValue: '-' });

  const averageGuests =
    typeof totals.averageGuestCount === 'number' && Number.isFinite(totals.averageGuestCount)
      ? Math.round(totals.averageGuestCount).toLocaleString()
      : t('dashboard:reports.catering.guestsUnavailable', { defaultValue: '-' });

  const upcomingList = upcoming.slice(0, 4).map((item) => ({
    id: item.id,
    name: item.name,
    status: item.status,
    budget: item.budgetCents !== null ? formatAmount(item.budgetCents) : '-',
    dateLabel: formatDistanceToNow(new Date(item.eventDate), { addSuffix: true }),
    dueStatus: item.dueStatus,
  }));

  return {
    pipelineValue,
    confirmedValue,
    outstandingValue,
    conversionLabel,
    averageGuests,
    payments,
    totals,
    upcoming: upcomingList,
  };
};
